import { Badge, type BadgeSize, type BadgeTone } from "@/components/ui/badge";
import { cn } from "@/lib/utils";
import type {
  AppStatus,
  ProviderState,
  BackupStatus,
  HostStatus,
  ProvisionStatus,
  ServiceStatus,
  TaskStatus
} from "@/types";

type OwnStatus =
  | AppStatus
  | HostStatus
  | TaskStatus
  | ServiceStatus
  | BackupStatus
  | ProvisionStatus;

export type StatusBadgeStatus = OwnStatus | ProviderState;

// Total Records: adding a member to any union above fails the build until it is given a tone.
const ownStatusTones: Record<OwnStatus, BadgeTone> = {
  active: "success",
  completed: "success",
  error: "destructive",
  queued: "warning",
  uploading: "warning",
  deleting: "warning",
  new: "info",
  created: "info",
  draft: "muted",
  archived: "muted",
  cancelled: "muted",
  removed: "muted"
};

const providerStateTones: Record<ProviderState, BadgeTone> = {
  running: "success",
  pending: "warning",
  stopped: "muted",
  missing: "destructive",
  unknown: "muted"
};

// A Map, not an object: lookup runs on values from String? columns, so "constructor" or "toString"
// would resolve off Object.prototype instead of falling through to muted.
const statusTones = new Map<string, BadgeTone>([
  ...Object.entries(ownStatusTones),
  ...Object.entries(providerStateTones)
]);

export const StatusBadge = ({
  status,
  customStatus,
  fallback = "unknown",
  size,
  className,
  ...props
}: Omit<React.ComponentProps<typeof Badge>, "tone" | "variant" | "size" | "children"> & {
  status: StatusBadgeStatus | null | undefined;
  /** Word to print instead of `status`; `status` still picks the colour. */
  customStatus?: string;
  /** Pass "draft" only for apps and hosts, the two that declare it. */
  fallback?: StatusBadgeStatus;
  size?: BadgeSize;
}) => {
  // `||` not `??`: the columns are String?, so "" is reachable and would render an empty pill.
  const tone = status || fallback;

  return (
    <Badge
      data-status={tone}
      tone={statusTones.get(tone.toLowerCase()) ?? "muted"}
      size={size}
      className={cn("capitalize", className)}
      {...props}
    >
      {customStatus || tone}
    </Badge>
  );
};
